import { useEffect, useState } from 'react';

import { getLabelForAITranslation, getLabelForOriginalTranslation } from 'js/lib/languageLocalized';

import StorageUtils from 'bundles/interactive-transcript/utils/StorageUtils';
import type { VideoPlayer } from 'bundles/item-lecture';
// eslint-disable-next-line @coursera/custom-rules/no-private-bundle-imports
import { useDubbingContext } from 'bundles/item-lecture/providers/DubbingProvider';
import type { DubbedSources, SourcesByResolution } from 'bundles/video-player/types/VideoContent';
import type { PossibleResolution, ResolutionOption } from 'bundles/video-player/types/VideoPlayer';
import { saveResolutionInUserPreferences } from 'bundles/video-player/utils/resolutionUtils';
import userPreferences from 'bundles/video-player/utils/userPreferences';

type Props = {
  dubbedSources?: DubbedSources;
  player: VideoPlayer;
  originalSources: SourcesByResolution;
  defaultLanguageKey?: string;
  setShouldShowNudge: (shouldShowNudge: boolean) => void;
  getDubbedSubtitleKeys: () => string[];
  shouldShowNudgeOnMatchingUserLanguage: () => void;
  setCurrentResolutionOption: (resolutionOption: ResolutionOption) => void;
  setCurrentResolution: (resolution: PossibleResolution) => void;
  currentResolution: PossibleResolution | '';
  enableHls?: boolean;
  hlsSource?: string;
};

const useDubbing = ({
  dubbedSources,
  player,
  originalSources,
  defaultLanguageKey,
  setShouldShowNudge,
  getDubbedSubtitleKeys,
  shouldShowNudgeOnMatchingUserLanguage,
  setCurrentResolutionOption,
  setCurrentResolution,
  enableHls,
  hlsSource,
  currentResolution: currentlyRenderedResolution,
}: Props) => {
  const [selectedAudioLanguage, setSelectedAudioLanguage] = useState({
    id: '',
    label: '',
    languageKey: '',
  });
  const {
    setShouldChangeTranscriptTracks,
    setShouldChangeTranscriptLanguage,
    setReportIssueSectionVisible,
    setChangeSubtitleOnAudioChange,
    isDubbingAvailable,
  } = useDubbingContext();

  useEffect(() => {
    setSelectedAudioLanguage({
      id: defaultLanguageKey ?? '',
      label: getLabelForOriginalTranslation(defaultLanguageKey ?? ''),
      languageKey: defaultLanguageKey ?? '',
    });
  }, [defaultLanguageKey]);

  const onDubbedAudioChoiceClick = (
    option: { id: string; languageKey: string; label: string },
    calledOnPageLoad = false
  ) => {
    // hide the user education nudge
    setShouldShowNudge(false);
    // contain video soruce
    let videoSource;
    // boolean to determine if the report section should be visible
    let reportSectionVisible;
    // boolean to determine if the original video is selected
    const isOriginalVideoSelected = option.languageKey === defaultLanguageKey;

    const isOriginalVideoAndIsHlsEnabled = !!(isOriginalVideoSelected && enableHls && hlsSource);

    // Sometimes the current visually rendered resolution can be set to an HLS resolution that does not exist for mp4 sources,
    // so we need to check if it is supported. If it is not, we will default to the most recently manually selected resolution stored in the player.
    const currentResolution = player.getSupportedResolutions().includes(currentlyRenderedResolution)
      ? currentlyRenderedResolution
      : player.resolution();

    // if the original video is selected, set the video source to the original sources and set the report section to false
    if (isOriginalVideoSelected) {
      // We will now use mp4 sources to control manual resolutions, so we only want to use the HLS source if the original video is selected. This is done
      // to further surface auto-resolution switching to our users when we run the HLS experiment.
      videoSource = isOriginalVideoAndIsHlsEnabled ? hlsSource : originalSources[currentResolution]?.['video/mp4'];
      // if the video source is not found, return
      if (!videoSource) return;
      // change the audio language
      setSelectedAudioLanguage(option);
      reportSectionVisible = false;
      // set the audio language to none
      userPreferences.set('audioLanguage', 'none');
      // set the dubbed subtitle language to none
      userPreferences.set('dubbedSubtitleLanguage', 'none');
      // show the user education nudge if the global language matches any one of the dubbed languages
      shouldShowNudgeOnMatchingUserLanguage();
    } else {
      // set the video source to the dubbed sources for the selected language and resolution
      videoSource = dubbedSources?.[option.languageKey]?.[currentResolution]?.mp4VideoUrl;
      if (!videoSource) return;
      // change the audio language
      setSelectedAudioLanguage(option);
      // set the audio language to the selected language
      userPreferences.set('audioLanguage', option.languageKey);
      // set the report section to true
      reportSectionVisible = true;
    }
    // if the video source is found
    if (videoSource) {
      // fetch the current time, playback rate, and paused state
      const currentTime = player.currentTime();
      const playbackRate = player.playbackRate();
      const paused = player.paused();
      // on loadedmetadata, set the current time, playback rate, and paused state
      player.one('loadedmetadata', () => {
        // I have discovered a race condition where the actual player current time when this callback is run is not as accurate as the one
        // stored in the currentTime variable set above. In this instance, use the most recent current time to set the player's current time.
        const mostRecentCurrentTime = Math.max(player.currentTime(), currentTime);

        // set the current time, playback rate, and paused state
        player.currentTime(mostRecentCurrentTime);
        player.playbackRate(playbackRate);
        const autoplay = userPreferences.get('autoplay');
        // if the video was paused, keep it paused, otherwise play it
        if (paused && !autoplay) {
          player.pause();
        } else {
          player.play();
        }

        // determine the transcript language code
        let transcriptLanguageCode;
        // if the function was called on page load
        if (calledOnPageLoad) {
          // fetch the dubbed transcript language code
          const dubbedTranscriptLanguageCode = StorageUtils.get('dubbed-language-code');
          // if the dubbed transcript language code is none, set the transcript language code fetched from the language-code storage
          transcriptLanguageCode =
            dubbedTranscriptLanguageCode === 'none' ? StorageUtils.get('language-code') : dubbedTranscriptLanguageCode;
        } else if (isOriginalVideoSelected) {
          // if the original video is selected, set the transcript language code fetched from the language-code storage
          transcriptLanguageCode = StorageUtils.get('language-code');
          // set the dubbed transcript language code to none
          StorageUtils.set('dubbed-language-code', 'none');
        } else {
          // if the original video is not selected, set the transcript language code to the selected language code
          transcriptLanguageCode = option.languageKey;
          // set the dubbed transcript language code to the selected language code
          StorageUtils.set('dubbed-language-code', transcriptLanguageCode);
        }
        // trigger the transcript language change
        setShouldChangeTranscriptLanguage({
          shouldChange: true,
          languageCode: transcriptLanguageCode,
        });
        // trigger the subtitle change
        setChangeSubtitleOnAudioChange({
          shouldChange: true,
          languageCode: option.languageKey,
        });
        // set the report section to the report section visible based on the boolean
        setReportIssueSectionVisible(reportSectionVisible);
      });
      // trigger the transcript tracks change
      setShouldChangeTranscriptTracks({
        shouldChange: option.languageKey !== defaultLanguageKey,
        languageCode: option.languageKey,
      });
      // pause the video
      player.pause();
      // set the video source to the video source
      player.src({
        src: videoSource,
        type: isOriginalVideoAndIsHlsEnabled ? 'application/x-mpegURL' : 'video/mp4',
      });

      // If the original video is selected, this means we will be using the HLS source, so we need to set the resolution option to auto to let the user know.
      if (isOriginalVideoAndIsHlsEnabled) {
        setCurrentResolutionOption('auto');
        saveResolutionInUserPreferences('auto');
      } else {
        // If the source we switched to is not the HLS source, we need to set the resolution option to the current stored mp4 resolution.
        setCurrentResolutionOption(currentResolution);
        saveResolutionInUserPreferences(currentResolution);
      }
    }
  };

  const getDubbedLanguagesList = () => {
    const languages = Object.keys(dubbedSources ?? {});
    const languagesList = languages.map((language) => {
      return {
        label: getLabelForAITranslation(language),
        languageKey: language,
      };
    });
    if (defaultLanguageKey) {
      languagesList.unshift({
        label: getLabelForOriginalTranslation(defaultLanguageKey),
        languageKey: defaultLanguageKey,
      });
    }
    return languagesList.sort((a, b) => a.label.localeCompare(b.label));
  };

  const handleResolutionChangeForDubbedAudio = (resolutionOption: ResolutionOption) => {
    const src = dubbedSources?.[selectedAudioLanguage.languageKey]?.[resolutionOption]?.mp4VideoUrl;
    // We don't show the auto option for dubbed audio sources, so we don't need to handle the auto case here. This also removes the need for any explicit typecasting.
    if (src && resolutionOption !== 'auto') {
      const currentTime = player.currentTime();
      const playbackRate = player.playbackRate();
      const paused = player.paused();
      player.one('loadedmetadata', () => {
        player.currentTime(currentTime);
        player.playbackRate(playbackRate);
        const autoplay = userPreferences.get('autoplay');
        if (paused && !autoplay) {
          player.pause();
        } else {
          player.play();
        }
      });
      player.resolution(resolutionOption, { skipSrcLoadingLogic: true });

      saveResolutionInUserPreferences(resolutionOption);
      player.src({ src, type: 'video/mp4' });

      setCurrentResolutionOption(resolutionOption);
      setCurrentResolution(resolutionOption);
    }
  };

  useEffect(() => {
    if (!isDubbingAvailable) return;
    // preserve the audio language preference and play the audio in the user preferred language
    const audioLanguage = userPreferences.get('audioLanguage');
    if (audioLanguage === 'none' || audioLanguage === undefined) return;
    const dubbedSubtitleKeys = getDubbedSubtitleKeys();
    const isAudioSupported = audioLanguage && dubbedSubtitleKeys.includes(audioLanguage);
    if (!isAudioSupported) return;
    const audio = {
      id: audioLanguage,
      label: getLabelForAITranslation(audioLanguage),
      languageKey: audioLanguage,
    };

    // Nest this inside of player.ready so that it takes precedence over the player.ready call in VideoPlayer.tsx. This ensures that whenever
    // dubbed audio is available and should be played, it will be played over any other HLS or mp4 source.
    player.ready(() => {
      onDubbedAudioChoiceClick(audio, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDubbingAvailable]);

  return {
    selectedAudioLanguage,
    onDubbedAudioChoiceClick,
    getDubbedLanguagesList,
    handleResolutionChangeForDubbedAudio,
  };
};

export { useDubbing };
